import type { LifeStage } from '@tama/game-core';
import type { Graphics } from 'pixi.js';
import { TILE } from './layout';
import { C } from './palette';
import type { CatAnim } from './walker';

/**
 * A tuxedo cat — black coat, white muzzle, bib and paws — drawn in a kawaii
 * register: oversized head, big eyes, short limbs, blush marks.
 *
 * Everything is drawn in local space with the feet at (0, 0) and up being
 * negative. The caller positions, scales and mirrors the Graphics, so nothing in
 * here needs to know where in the room the cat is.
 */

/** Kittens start small and fill out as they grow up. */
const STAGE_SCALE: Record<LifeStage, number> = {
  baby: 0.78,
  child: 0.88,
  teen: 0.96,
  adult: 1.04,
};

/**
 * The cat is authored about 100 units tall; on the grid an adult should stand a
 * touch under a tile and a half, which is the RPG Maker proportion — tall enough
 * to have a face, short enough that it never hides the tile it is standing on.
 */
const GRID_SCALE = (TILE * 1.45) / 100;

export function catScaleFor(stage: LifeStage): number {
  return STAGE_SCALE[stage] * GRID_SCALE;
}

export interface CatDrawInput {
  anim: CatAnim;
  /** Seconds spent in this pose. */
  animTime: number;
}

export function drawCat(g: Graphics, { anim, animTime }: CatDrawInput): void {
  g.clear();

  switch (anim) {
    case 'walk':
      drawWalking(g, animTime);
      break;
    case 'sleep':
      drawSleeping(g, animTime);
      break;
    case 'hop':
      drawHopping(g, animTime);
      break;
    case 'sit':
      drawSitting(g, animTime, 0);
      break;
  }
}

// ------------------------------------------------------------------- helpers

/**
 * Eyes are open most of the time and snap shut briefly — a blink is short and
 * occasional, so it is driven by a fast sine thresholded near its peak rather
 * than by a timer.
 */
function blinkAmount(t: number): number {
  const wave = Math.sin(t * 1.9) * Math.sin(t * 0.7 + 1.3);
  return wave > 0.94 ? 1 : 0;
}

function shadow(g: Graphics, rx: number, ry = rx * 0.28): void {
  g.ellipse(0, 2, rx, ry).fill({ color: C.woodDark, alpha: 0.2 });
}

/** The face, shared by every upright pose. `openness` 0…1 drives the blink. */
function face(g: Graphics, cx: number, cy: number, r: number, openness: number): void {
  // Ears, drawn behind the head so their bases disappear under it.
  const earSpan = r * 0.72;
  for (const side of [-1, 1] as const) {
    const bx = cx + side * earSpan;
    g.poly([
      bx - r * 0.26,
      cy - r * 0.5,
      bx + side * r * 0.1,
      cy - r * 1.28,
      bx + r * 0.26,
      cy - r * 0.5,
    ]).fill(C.catFur);
    g.poly([
      bx - r * 0.13,
      cy - r * 0.55,
      bx + side * r * 0.06,
      cy - r * 1.03,
      bx + r * 0.13,
      cy - r * 0.55,
    ]).fill(C.catInnerEar);
  }

  g.circle(cx, cy, r).fill(C.catFur);

  // White muzzle: two overlapping rounds, Sylvester-style.
  g.ellipse(cx - r * 0.26, cy + r * 0.42, r * 0.36, r * 0.3).fill(C.catWhite);
  g.ellipse(cx + r * 0.26, cy + r * 0.42, r * 0.36, r * 0.3).fill(C.catWhite);
  // ...continuing up between the eyes as a blaze.
  g.ellipse(cx, cy + r * 0.1, r * 0.24, r * 0.55).fill(C.catWhite);

  // Eyes.
  const eyeX = r * 0.42;
  const eyeY = cy - r * 0.12;
  const eyeR = r * 0.27;
  for (const side of [-1, 1] as const) {
    const x = cx + side * eyeX;
    if (openness <= 0.05) {
      // Closed: a happy upward arc.
      g.moveTo(x - eyeR, eyeY)
        .quadraticCurveTo(x, eyeY - eyeR * 0.9, x + eyeR, eyeY)
        .stroke({ color: C.catWhite, width: Math.max(1.4, r * 0.075), cap: 'round' });
      continue;
    }
    const ry = eyeR * openness;
    g.ellipse(x, eyeY, eyeR * 0.86, ry).fill(C.eyeWhite);
    g.ellipse(x, eyeY, eyeR * 0.58, ry * 0.78).fill(C.eyeIris);
    g.ellipse(x, eyeY, eyeR * 0.26, ry * 0.62).fill(C.eyePupil);
    g.circle(x - eyeR * 0.28, eyeY - ry * 0.34, eyeR * 0.2).fill(C.eyeWhite);
  }

  // Nose and mouth.
  const noseY = cy + r * 0.26;
  g.poly([cx - r * 0.12, noseY, cx + r * 0.12, noseY, cx, noseY + r * 0.13]).fill(C.catNose);
  const mouthY = noseY + r * 0.16;
  const mouthW = r * 0.16;
  for (const side of [-1, 1] as const) {
    g.moveTo(cx, mouthY)
      .quadraticCurveTo(cx + side * mouthW, mouthY + r * 0.09, cx + side * mouthW * 1.5, mouthY)
      .stroke({ color: C.catFurShade, width: Math.max(1, r * 0.05), cap: 'round' });
  }

  // Blush and whiskers.
  g.ellipse(cx - r * 0.72, cy + r * 0.3, r * 0.17, r * 0.11).fill({
    color: C.catBlush,
    alpha: 0.75,
  });
  g.ellipse(cx + r * 0.72, cy + r * 0.3, r * 0.17, r * 0.11).fill({
    color: C.catBlush,
    alpha: 0.75,
  });

  for (const side of [-1, 1] as const) {
    for (const [dy, len] of [
      [-0.06, 0.95],
      [0.1, 1],
      [0.26, 0.92],
    ] as const) {
      g.moveTo(cx + side * r * 0.52, cy + r * dy)
        .lineTo(cx + side * r * len * 1.35, cy + r * (dy - 0.06))
        .stroke({ color: C.catWhite, width: Math.max(0.8, r * 0.035), alpha: 0.85, cap: 'round' });
    }
  }
}

/**
 * A tail curling out to the cat's side and swaying with `sway`.
 *
 * It sweeps wide rather than straight back, otherwise the body hides it
 * completely in the sitting pose and the cat looks tailless.
 */
function tail(g: Graphics, baseX: number, baseY: number, sway: number, scale = 1): void {
  const w = 9 * scale;
  const tipX = baseX + (26 + sway * 4) * scale;
  const tipY = baseY - (26 + sway * 8) * scale;

  g.moveTo(baseX, baseY)
    .quadraticCurveTo(baseX + 34 * scale, baseY + 4 * scale, tipX, tipY)
    .stroke({ color: C.catFur, width: w, cap: 'round' });
  // White tip — the tuxedo signature.
  g.circle(tipX, tipY, w * 0.5).fill(C.catWhite);
}

// --------------------------------------------------------------------- poses

/** Sitting upright, three-quarter view. The default resting pose. */
function drawSitting(g: Graphics, t: number, lift: number): void {
  const breathe = Math.sin(t * 2.1) * 0.02;
  const y = -lift;

  shadow(g, 30 - lift * 0.25);
  tail(g, 16, y - 8, Math.sin(t * 1.5));

  // Haunches and body.
  g.ellipse(0, y - 30, 30, 32 * (1 + breathe)).fill(C.catFur);
  g.ellipse(-22, y - 8, 13, 9).fill(C.catFur);
  g.ellipse(22, y - 8, 13, 9).fill(C.catFur);
  g.ellipse(-24, y - 5, 8, 5).fill(C.catWhite); // back paws
  g.ellipse(24, y - 5, 8, 5).fill(C.catWhite);

  // White bib down the chest.
  g.ellipse(0, y - 24, 15, 21).fill(C.catWhite);

  // Front legs with white socks.
  for (const side of [-1, 1] as const) {
    g.roundRect(side * 10 - 5, y - 22, 10, 22, 5).fill(C.catFur);
    g.ellipse(side * 10, y - 3, 7, 5).fill(C.catWhite);
  }

  face(g, 0, y - 68, 27, blinkAmount(t) > 0 ? 0 : 1);
}

/** Side-on walking cycle. */
function drawWalking(g: Graphics, t: number): void {
  const cycle = t * 7.5;
  const bob = Math.sin(cycle * 2) * 1.6;
  const y = -bob;

  shadow(g, 32);

  // Legs: front and back pairs in opposite phase.
  const legs: [number, number][] = [
    [-20, 0],
    [-9, Math.PI],
    [11, Math.PI],
    [22, 0],
  ];
  for (const [x, phase] of legs) {
    const swing = Math.sin(cycle + phase);
    const lift = Math.max(0, Math.sin(cycle + phase)) * 5;
    g.roundRect(x - 4.5 + swing * 3, y - 18, 9, 18 - lift, 4.5).fill(C.catFur);
    g.ellipse(x + swing * 3, y - 1 - lift, 6.5, 4.5).fill(C.catWhite);
  }

  // Tail up and waving, then the body over the legs.
  g.moveTo(-26, y - 30)
    .quadraticCurveTo(-46, y - 40, -42 + Math.sin(cycle) * 4, y - 62)
    .stroke({ color: C.catFur, width: 9, cap: 'round' });
  g.circle(-42 + Math.sin(cycle) * 4, y - 62, 4.5).fill(C.catWhite);

  g.ellipse(-2, y - 32, 34, 21).fill(C.catFur);
  g.ellipse(2, y - 25, 24, 11).fill(C.catWhite); // belly

  face(g, 28, y - 46, 22, blinkAmount(t) > 0 ? 0 : 1);
}

/** Curled up asleep, with drifting Zs. */
function drawSleeping(g: Graphics, t: number): void {
  const breathe = Math.sin(t * 1.5) * 0.035;

  shadow(g, 40, 11);

  // Body curled into a comma.
  g.ellipse(0, -20, 40, 22 * (1 + breathe)).fill(C.catFur);
  g.ellipse(6, -14, 26, 12).fill(C.catWhite);

  // Tail wrapped around the front.
  g.moveTo(-34, -18)
    .quadraticCurveTo(-14, -2, 18, -8)
    .stroke({ color: C.catFur, width: 9, cap: 'round' });
  g.circle(18, -8, 4.5).fill(C.catWhite);

  // Head tucked against the body, always with closed eyes.
  const cx = 26;
  const cy = -30 - breathe * 10;
  const r = 21;

  for (const side of [-1, 1] as const) {
    const bx = cx + side * r * 0.7;
    g.poly([
      bx - r * 0.26,
      cy - r * 0.5,
      bx + side * r * 0.1,
      cy - r * 1.25,
      bx + r * 0.26,
      cy - r * 0.5,
    ]).fill(C.catFur);
    g.poly([
      bx - r * 0.12,
      cy - r * 0.54,
      bx + side * r * 0.06,
      cy - r,
      bx + r * 0.12,
      cy - r * 0.54,
    ]).fill(C.catInnerEar);
  }

  g.circle(cx, cy, r).fill(C.catFur);
  g.ellipse(cx - r * 0.24, cy + r * 0.44, r * 0.34, r * 0.28).fill(C.catWhite);
  g.ellipse(cx + r * 0.24, cy + r * 0.44, r * 0.34, r * 0.28).fill(C.catWhite);
  g.ellipse(cx, cy + r * 0.12, r * 0.22, r * 0.5).fill(C.catWhite);

  for (const side of [-1, 1] as const) {
    const x = cx + side * r * 0.42;
    g.moveTo(x - r * 0.24, cy - r * 0.1)
      .quadraticCurveTo(x, cy + r * 0.14, x + r * 0.24, cy - r * 0.1)
      .stroke({ color: C.catWhite, width: 1.8, cap: 'round' });
  }
  g.poly([cx - r * 0.11, cy + r * 0.28, cx + r * 0.11, cy + r * 0.28, cx, cy + r * 0.41]).fill(
    C.catNose,
  );
  g.ellipse(cx - r * 0.7, cy + r * 0.32, r * 0.16, r * 0.1).fill({ color: C.catBlush, alpha: 0.7 });
  g.ellipse(cx + r * 0.7, cy + r * 0.32, r * 0.16, r * 0.1).fill({ color: C.catBlush, alpha: 0.7 });

  drawZzz(g, t, cx + 18, cy - 26);
}

function drawZzz(g: Graphics, t: number, x: number, y: number): void {
  for (let i = 0; i < 3; i++) {
    const phase = (t * 0.45 + i * 0.33) % 1;
    const size = 5 + i * 1.6;
    const zx = x + phase * 20 + i * 3;
    const zy = y - phase * 34;
    const alpha = Math.sin(phase * Math.PI) * 0.9;
    g.moveTo(zx, zy)
      .lineTo(zx + size, zy)
      .lineTo(zx, zy + size)
      .lineTo(zx + size, zy + size)
      .stroke({ color: C.zzz, width: 2, alpha, cap: 'round', join: 'round' });
  }
}

/** A pleased little hop, with sparkles. */
function drawHopping(g: Graphics, t: number): void {
  const hop = Math.abs(Math.sin(t * 4.2));
  drawSitting(g, t, hop * 16);

  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + t * 2;
    const rad = 34 + hop * 12;
    const x = Math.cos(a) * rad;
    const y = -46 + Math.sin(a) * rad * 0.5;
    const s = 2.4 + Math.sin(t * 6 + i) * 0.8;
    g.poly([x, y - s * 2, x + s, y, x, y + s * 2, x - s, y]).fill({
      color: C.sparkle,
      alpha: 0.55 + hop * 0.35,
    });
  }
}
